/**
 * Model detail — usage drill-down for a single LLM model (opened from the Model
 * prices table). Shows summary stats + time-series charts (calls, cost, latency,
 * tokens) for the team's spans on that model. Defaults to the last 30 days with
 * a time-period selector for other ranges.
 *
 * Chart strokes/grid use themed tokens: the previous version hardcoded
 * '#94a3b8' for the secondary series and drew the grid in --surface-hover,
 * which is invisible against the page in dark mode.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis,
} from 'recharts';
import MeasuredBox from 'shared/components/ui/MeasuredBox';
import { useTeam } from 'shared/context/teamContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import {
  fetchModelMetrics, listModelPrices, deleteModelPrice, mergeModelPrice,
} from 'features/settings/api/modelPricesApi';
import ModelPriceModal from 'features/settings/components/ModelPriceModal';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';
import { settingsSolidButtonClass } from 'features/settings/components/settingsTheme';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';

const INK = 'var(--text-primary)';
const MUTED = 'var(--text-muted)';

const C_PRIMARY = 'var(--brand-primary)';
const C_SECONDARY = 'var(--text-muted)';

const last30 = () => ({
  from: new Date(Date.now() - 30 * 86_400_000).toISOString(),
  to: new Date().toISOString(),
});

function fmtCost(v) {
  const n = Number(v) || 0;
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}
function fmtNum(v) {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function fmtMs(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`;
  return `${Math.round(n)}ms`;
}
function fmtPct(v) {
  return `${((Number(v) || 0) * 100).toFixed(1)}%`;
}

function StatRow({ items }) {
  return (
    <dl className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-4 py-3">
          <dt className="text-[12px] font-medium" style={{ color: INK }}>{label}</dt>
          <dd className="mt-2 text-[24px] font-semibold leading-none tabular-nums" style={{ color: INK }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ChartBlock({ title, children }) {
  return (
    <section className="flex flex-col overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-3 pb-3 pt-2.5">
      <div className="mb-2 text-[12px] font-medium" style={{ color: INK }}>{title}</div>
      <div className="h-[210px] w-full">
        <MeasuredBox>{children}</MeasuredBox>
      </div>
    </section>
  );
}

const AXIS = { stroke: 'var(--text-muted)', fontSize: 10 };

/**
 * Hover card: the bucket's full time, then one row per series with its swatch.
 * `series` is [{ key, name, color, format }]; rows read from the hovered bucket
 * so a missing value (no calls in that bucket) shows a dash rather than 0.
 */
function ChartTip({ active, payload, series }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div
      className="rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 py-2 shadow-lg"
      style={{ minWidth: 140 }}
    >
      <div className="mb-1.5 text-[11px] font-medium" style={{ color: 'var(--text-secondary)' }}>{row.full}</div>
      {series.map((sr) => (
        <div key={sr.key} className="flex items-center justify-between gap-4 text-[11px]">
          <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
            <span className="h-2 w-2 rounded-sm" style={{ background: sr.color, opacity: sr.opacity ?? 1 }} />
            {sr.name}
          </span>
          <span className="font-semibold tabular-nums" style={{ color: INK }}>
            {row[sr.key] == null ? '—' : sr.format(row[sr.key])}
          </span>
        </div>
      ))}
    </div>
  );
}

const ModelDetailPanel = ({ model: modelProp }) => {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id || selectedTeam?._id || null;
  const navigate = useNavigate();
  const { team, model: modelParam } = useParams();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.CONFIG_MODEL_PRICES_MANAGE);
  const model = modelProp || (modelParam ? decodeURIComponent(modelParam) : '');

  const [range, setRange] = useState(last30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceRow, setPriceRow] = useState(null);
  // The detected (unconfigured) row for this model. It carries the rates the
  // model is ALREADY being costed at, so "Set prices" can start from them.
  const [detectedRow, setDetectedRow] = useState(null);
  const [allPrices, setAllPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [setPriceOpen, setSetPriceOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);

  const load = useCallback(async () => {
    if (!teamId || !model) return;
    setLoading(true);
    setError(null);
    try {
      setData(await fetchModelMetrics(teamId, model, range));
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [teamId, model, range]);

  useEffect(() => { load(); }, [load]);

  // Load this model's configured price row + the full list (merge targets).
  const loadPriceRow = useCallback(async () => {
    if (!teamId || !model) return;
    setPricesLoading(true);
    try {
      const all = await listModelPrices(teamId);
      setAllPrices(all);
      setPriceRow(all.find((r) => r.configured && r.model_pattern === model) || null);
      setDetectedRow(all.find((r) => !r.configured && r.model_pattern === model) || null);
    } catch (e) {
      // Non-fatal for the charts, but say so — silently hiding every action
      // looks identical to "this model can't be edited".
      setError((prev) => prev || 'Could not load pricing for this model, so edit actions are hidden.');
    } finally {
      setPricesLoading(false);
    }
  }, [teamId, model]);

  useEffect(() => { loadPriceRow(); }, [loadPriceRow]);

  const mergeTargets = useMemo(
    () => allPrices.filter((r) => r.configured && r.model_pattern !== model),
    [allPrices, model],
  );

  // `hint` is searchable in BrandDropdown, so provider is matchable too.
  const mergeOptions = useMemo(
    () => mergeTargets.map((t) => ({
      value: String(t.id),
      label: t.model_pattern,
      hint: t.provider || undefined,
      monospace: true,
    })),
    [mergeTargets],
  );

  const doMerge = async () => {
    if (!mergeTarget) return;
    setMerging(true);
    try {
      await mergeModelPrice(teamId, Number(mergeTarget), model);
      const target = allPrices.find((r) => String(r.id) === String(mergeTarget));
      navigate(`/${team}/settings/model-prices/${encodeURIComponent(target?.model_pattern || '')}`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Merge failed');
      setMerging(false);
    }
  };

  const doDelete = async () => {
    if (!priceRow) return;
    setDeleting(true);
    try {
      await deleteModelPrice(teamId, priceRow.id);
      navigate(`/${team}/settings/model-prices`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Delete failed');
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const hourly = (data?.bucket_seconds || 86_400) < 86_400;
  const chartData = useMemo(
    () => (data?.buckets || []).map((b) => {
      const d = new Date(b.bucket);
      return {
        ...b,
        label: format(d, hourly ? 'MMM d HH:mm' : 'MMM d'),
        full: format(d, hourly ? 'MMM d, HH:mm' : 'MMM d, yyyy'),
      };
    }),
    [data, hourly],
  );

  const s = data?.summary;
  const xAxis = <XAxis dataKey="label" {...AXIS} tickLine={false} axisLine={{ stroke: 'var(--border-default)' }} minTickGap={32} />;
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />;
  const yAxis = (fmt) => <YAxis {...AXIS} tickLine={false} axisLine={false} width={46} tickFormatter={fmt} />;
  const cursor = { fill: 'var(--surface-hover)', opacity: 0.5 };
  const tip = (series) => <Tooltip cursor={cursor} content={<ChartTip series={series} />} isAnimationActive={false} />;
  const SERIES = {
    calls: [{ key: 'calls', name: 'Calls', color: C_PRIMARY, format: fmtNum }],
    cost: [{ key: 'cost', name: 'Cost', color: C_PRIMARY, format: fmtCost }],
    latency: [
      { key: 'avg_latency_ms', name: 'Avg latency', color: C_PRIMARY, format: fmtMs },
      { key: 'p95_latency_ms', name: 'p95 latency', color: C_SECONDARY, format: fmtMs },
    ],
    tokens: [
      { key: 'input_tokens', name: 'Input', color: C_PRIMARY, opacity: 0.4, format: fmtNum },
      { key: 'output_tokens', name: 'Output', color: C_PRIMARY, format: fmtNum },
    ],
  };

  return (
    <div style={{ color: INK, fontFamily: "'Inter', sans-serif" }}>
      {/* The model name lives in the top-bar path (Settings / Model prices /
          <model>), so no in-panel heading: just a way back. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <EventsTimeFilter
          selectedPreset="30d"
          onTimeFilterChange={(f) => f?.from && f?.to && setRange({ from: f.from, to: f.to })}
        />
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Detected/unpriced model → offer to set prices (materialises a row). */}
            {!priceRow && (
              <button
                type="button"
                onClick={() => setSetPriceOpen(true)}
                className={settingsSolidButtonClass}
              >
                Set prices
              </button>
            )}
            {/* Merge this model into another configured model (as an alias).
                BrandDropdown rather than a native <select>: the OS renders
                <option> lists in its own chrome, which ignores the theme. */}
            {(mergeTargets.length > 0 || pricesLoading) && !confirmingDelete && (
              <div className="inline-flex items-center gap-1.5">
                <div className="w-[190px]">
                  <BrandDropdown
                    compact
                    value={mergeTarget}
                    onChange={setMergeTarget}
                    options={mergeOptions}
                    placeholder="Merge into…"
                    triggerClassName="!h-8 !rounded-md !bg-surface !px-3 !py-0 !text-[12px] !font-medium"
                    searchable
                    searchPlaceholder="Search models…"
                    // Keep the initial list short; search reaches every model.
                    visibleLimit={15}
                    loading={pricesLoading}
                    menuMinWidth={260}
                  />
                </div>
                {mergeTarget && (
                  <button type="button" onClick={doMerge} disabled={merging} className={activityNeutralButtonClass}>
                    {merging ? 'Merging…' : 'Merge'}
                  </button>
                )}
              </div>
            )}
            {priceRow && !confirmingDelete && (
              <>
                <button type="button" onClick={() => setEditOpen(true)} className={activityNeutralButtonClass}>
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  className={`${activityNeutralButtonClass} !text-[color:var(--feedback-error)]`}
                >
                  Delete
                </button>
              </>
            )}
            {priceRow && confirmingDelete && (
              <div className="inline-flex items-center gap-2">
                <span className="text-[11px]" style={{ color: MUTED }}>Delete this price?</span>
                <button
                  type="button"
                  onClick={doDelete}
                  disabled={deleting}
                  className={`${activityNeutralButtonClass} !text-[color:var(--feedback-error)]`}
                >
                  {deleting ? 'Deleting…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleting}
                  className={activityNeutralButtonClass}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mb-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>
      )}

      <StatRow
        items={[
          ['Calls', loading ? '—' : fmtNum(s?.calls)],
          ['Cost', loading ? '—' : fmtCost(s?.cost)],
          ['Input tokens', loading ? '—' : fmtNum(s?.input_tokens)],
          ['Output tokens', loading ? '—' : fmtNum(s?.output_tokens)],
          ['Avg latency', loading ? '—' : fmtMs(s?.avg_latency_ms)],
          ['p95 latency', loading ? '—' : fmtMs(s?.p95_latency_ms)],
          ['Error rate', loading ? '—' : fmtPct(s?.error_rate)],
        ]}
      />

      {loading && !data ? (
        <BrandLoaderInline label="Loading usage" />
      ) : (data?.buckets || []).length === 0 ? (
        <p className="py-10 text-center text-[12px]" style={{ color: MUTED }}>No usage in this period</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ChartBlock title="Calls">
            {({ width, height }) => (
              <BarChart width={width} height={height} data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                {grid}{xAxis}{yAxis(fmtNum)}{tip(SERIES.calls)}
                <Bar dataKey="calls" fill={C_PRIMARY} maxBarSize={28} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              </BarChart>
            )}
          </ChartBlock>

          <ChartBlock title="Cost">
            {({ width, height }) => (
              <BarChart width={width} height={height} data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                {grid}{xAxis}{yAxis(fmtCost)}{tip(SERIES.cost)}
                <Bar dataKey="cost" fill={C_PRIMARY} maxBarSize={28} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              </BarChart>
            )}
          </ChartBlock>

          <ChartBlock title="Latency (avg, p95)">
            {({ width, height }) => (
              <LineChart width={width} height={height} data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                {grid}{xAxis}{yAxis(fmtMs)}{tip(SERIES.latency)}<Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
                <Line type="monotone" dataKey="avg_latency_ms" name="Avg latency" stroke={C_PRIMARY} strokeWidth={2} dot={chartData.length < 3} isAnimationActive={false} />
                <Line type="monotone" dataKey="p95_latency_ms" name="p95 latency" stroke={C_SECONDARY} strokeWidth={2} dot={chartData.length < 3} isAnimationActive={false} />
              </LineChart>
            )}
          </ChartBlock>

          <ChartBlock title="Tokens (input, output)">
            {({ width, height }) => (
              <BarChart width={width} height={height} data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                {grid}{xAxis}{yAxis(fmtNum)}{tip(SERIES.tokens)}<Legend wrapperStyle={{ fontSize: 11 }} iconType="square" />
                <Bar dataKey="input_tokens" name="Input" stackId="t" fill={C_PRIMARY} fillOpacity={0.4} maxBarSize={28} isAnimationActive={false} />
                <Bar dataKey="output_tokens" name="Output" stackId="t" fill={C_PRIMARY} maxBarSize={28} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              </BarChart>
            )}
          </ChartBlock>
        </div>
      )}

      {editOpen && priceRow && (
        <ModelPriceModal
          teamId={teamId}
          initial={priceRow}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); loadPriceRow(); }}
        />
      )}

      {setPriceOpen && !priceRow && (
        <ModelPriceModal
          teamId={teamId}
          // Seeded with the rates in use (catalog or Trodo default) rather than
          // an empty form: the user is overriding a real number, not inventing
          // one, and can add categories on top.
          initial={{
            id: null,
            model_pattern: model,
            provider: detectedRow?.provider || null,
            prices: detectedRow?.prices || {},
            aliases: [],
          }}
          seededFrom={detectedRow?.price_source}
          onClose={() => setSetPriceOpen(false)}
          onSaved={() => { setSetPriceOpen(false); loadPriceRow(); }}
        />
      )}
    </div>
  );
};

export default ModelDetailPanel;
